import React, { createContext, useContext, useMemo, useState, useEffect } from 'react';
import axios from 'axios';
import apiClient from '../axios';
import { DEFAULT_LANGUAGE, getInitialLanguage, LANGUAGES, markLanguageOverride, translations } from './translations';
import { getDanishDomesticCopy } from './danishDomesticCopy';
import { getDanishSpainCopy } from './danishSpainCopy';

const LanguageContext = createContext({
  language: DEFAULT_LANGUAGE,
  setLanguage: () => {},
  t: (key) => key,
  tRaw: (key) => key,
});

const readPath = (source, path) => {
  return path.split('.').reduce((value, part) => {
    if (!value || typeof value !== 'object') return undefined;
    return value[part];
  }, source);
};

const interpolate = (text, values = {}) => {
  return String(text).replace(/\{(\w+)\}/g, (_, key) => (
    Object.prototype.hasOwnProperty.call(values, key) ? values[key] : `{${key}}`
  ));
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(getInitialLanguage);

  const setLanguage = (nextLanguage) => {
    const normalized = LANGUAGES[nextLanguage] ? nextLanguage : DEFAULT_LANGUAGE;
    setLanguageState(normalized);
    window.localStorage.setItem('appLanguage', normalized);
    // The user explicitly chose a language; keep the language bar visible for
    // the rest of this session even on bodex.se.
    markLanguageOverride();
  };

  useEffect(() => {
    document.documentElement.lang = language;
    window.localStorage.setItem('appLanguage', language);
    axios.defaults.headers.common['X-App-Language'] = language;
    apiClient.defaults.headers.common['X-App-Language'] = language;
  }, [language]);

  const value = useMemo(() => {
    const translate = (key, values) => {
      const entry = readPath(translations, key);
      const domesticCopy = getDanishSpainCopy(key, language, values)
        ?? getDanishDomesticCopy(key, language, values);
      if (!entry && domesticCopy === undefined) return undefined;
      const translatedValue = domesticCopy ?? entry[language] ?? entry[DEFAULT_LANGUAGE];
      if (typeof translatedValue === 'string') {
        return interpolate(translatedValue, values);
      }
      return translatedValue;
    };

    return {
      language,
      setLanguage,
      t: (key, values) => {
      const translated = translate(key, values);
      return typeof translated === 'string' ? translated : key;
      },
      tRaw: (key, values) => translate(key, values),
      languageMeta: LANGUAGES[language],
    };
  }, [language]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
